import type { CustomFormAnswerItem } from '@/services/communityService';

/** Same as backend isGKRole: gk, goal keeper, keeper, kiper */
export function isGKRole(role: string): boolean {
  const r = (role ?? '').trim().toLowerCase();
  const gkKeywords = ['gk', 'goal keeper', 'keeper', 'kiper', 'goalkeeper'];
  return gkKeywords.some((keyword) =>
    keyword === 'gk'
      ? r === 'gk' || r.includes('(gk)') || /\bgk\b/.test(r)
      : r.includes(keyword)
  );
}

/** Extract effective position string from participant's custom form answer or fallback to position/role */
export function getParticipantEffectivePosition(p: {
  role?: string;
  position?: string;
  custom_form_answer?: CustomFormAnswerItem[];
}): string | undefined {
  if (p.custom_form_answer && Array.isArray(p.custom_form_answer)) {
    const posAns = p.custom_form_answer.find((ans) => {
      const qid = (ans.question_id || ans.field_id || '').toLowerCase();
      const title = (ans.title || '').toLowerCase();
      return (
        ans.template_type === 'form_role' ||
        ans.template_type === 'role' ||
        qid.includes('form_role') ||
        qid.includes('player_position') ||
        qid.includes('posisi') ||
        title.includes('posisi')
      );
    });
    if (posAns && typeof posAns.value === 'string' && posAns.value.trim() !== '') {
      return posAns.value.trim();
    }
  }
  return p.position || p.role;
}

/** Extract effective team name from participant's custom form answer or fallback to team */
export function getParticipantEffectiveTeam(p: {
  team?: string;
  custom_form_answer?: CustomFormAnswerItem[];
}): string | undefined {
  if (p.custom_form_answer && Array.isArray(p.custom_form_answer)) {
    const teamAns = p.custom_form_answer.find((ans) => {
      const qid = (ans.question_id || ans.field_id || '').toLowerCase();
      const title = (ans.title || '').toLowerCase();
      return qid === 'team' || qid.includes('team') || title === 'tim' || title.includes('tim');
    });
    if (teamAns && typeof teamAns.value === 'string' && teamAns.value.trim() !== '') {
      return teamAns.value.trim();
    }
  }
  return p.team;
}

/** Extract effective player number from participant's custom form answer or fallback to player_number */
export function getParticipantEffectivePlayerNumber(p: {
  player_number?: number | null;
  custom_form_answer?: CustomFormAnswerItem[];
}): number | null {
  if (p.custom_form_answer && Array.isArray(p.custom_form_answer)) {
    const numAns = p.custom_form_answer.find((ans) => {
      const qid = (ans.question_id || ans.field_id || '').toLowerCase();
      const title = (ans.title || '').toLowerCase();
      return (
        ans.template_type === 'player_number' ||
        qid === 'player_number' ||
        qid.includes('player_number') ||
        qid.includes('nomor_punggung') ||
        qid.includes('no_punggung') ||
        title === 'nomor punggung' ||
        title.includes('nomor punggung') ||
        title.includes('no punggung') ||
        title.includes('nomor jersey') ||
        title.includes('no jersey')
      );
    });
    if (numAns && numAns.value !== undefined && numAns.value !== null) {
      const valStr = String(numAns.value).trim();
      if (valStr !== '') {
        const parsed = parseInt(valStr, 10);
        if (!Number.isNaN(parsed) && parsed >= 0 && parsed <= 99) {
          return parsed;
        }
      }
    }
  }
  return typeof p.player_number === 'number' ? p.player_number : null;
}

/** Map user profile position, custom form answer, or game lineup role to valid role code (GK, DF, MF, FW, N) */
export function getDefaultRoleFromPosition(
  position?: string,
  fallbackRole?: string,
  _isFutsal?: boolean
): string {
  const parseRoleCode = (val?: string): string | null => {
    if (!val) return null;
    const vUpper = val.trim().toUpperCase();
    if (['FW', 'MF', 'DF', 'GK', 'N'].includes(vUpper)) {
      return vUpper;
    }
    const v = val.trim().toLowerCase();
    if (
      v === 'gk' ||
      v.includes('goalkeeper') ||
      v.includes('kiper') ||
      v.includes('(gk)')
    ) {
      return 'GK';
    }
    if (
      v.includes('defender') ||
      v.includes('bek') ||
      v.includes('back') ||
      v.includes('(df)')
    ) {
      return 'DF';
    }
    if (
      v.includes('midfielder') ||
      v.includes('gelandang') ||
      v.includes('midfield') ||
      v.includes('(mf)')
    ) {
      return 'MF';
    }
    if (
      v.includes('forward') ||
      v.includes('penyerang') ||
      v.includes('striker') ||
      v.includes('(fw)')
    ) {
      return 'FW';
    }
    return null;
  };

  // 1. Prioritize fallbackRole (saved game lineup role) if provided
  if (fallbackRole) {
    const code = parseRoleCode(fallbackRole);
    if (code) return code;
  }

  // 2. Parse position string (from custom form answer or profile position)
  if (position) {
    const code = parseRoleCode(position);
    if (code) return code;
  }

  // Default when no saved role or profile position exists: 'N'
  return 'N';
}

export type LineupSlot = {
  participantId: number;
  name: string;
  role: string;
  isStarting: boolean;
  playerNumber?: number | null;
};

export interface MabarParticipantForGenerate {
  id: number;
  name: string;
  role: string;
  position?: string;
  player_number?: number | null;
  custom_form_answer?: CustomFormAnswerItem[];
}

/**
 * Generate one team: 1 GK + (formatNumber - 1) non-GK.
 * Non-GK sorted by played count ascending (smallest first).
 * Returns { lineup, error }. error set if not enough GK or not enough players.
 */
export function generateOneTeam(
  pool: MabarParticipantForGenerate[],
  playedCountMap: Map<number, number>,
  formatNumber: number,
  isFutsal?: boolean
): { lineup: LineupSlot[]; error: string | null } {
  const gkList = pool.filter((p) => isGKRole(p.role) || isGKRole(p.position ?? ''));
  const nonGKList = pool.filter((p) => !isGKRole(p.role) && !isGKRole(p.position ?? ''));
  const needNonGK = formatNumber - 1;

  if (gkList.length < 1) {
    return {
      lineup: [],
      error: 'Minimal 1 pemain dengan role kiper diperlukan untuk generate otomatis.',
    };
  }
  if (nonGKList.length < needNonGK) {
    return {
      lineup: [],
      error: `Tidak cukup pemain non-kiper. Diperlukan ${needNonGK}, tersedia ${nonGKList.length}.`,
    };
  }

  const sortedNonGK = [...nonGKList].sort(
    (a, b) => (playedCountMap.get(a.id) ?? 0) - (playedCountMap.get(b.id) ?? 0)
  );

  const lineup: LineupSlot[] = [];
  const gk = gkList[0];
  lineup.push({
    participantId: gk.id,
    name: gk.name,
    role: getDefaultRoleFromPosition(gk.position, gk.role, isFutsal),
    isStarting: true,
    playerNumber: getParticipantEffectivePlayerNumber(gk),
  });
  for (let i = 0; i < needNonGK; i++) {
    const p = sortedNonGK[i];
    lineup.push({
      participantId: p.id,
      name: p.name,
      role: getDefaultRoleFromPosition(p.position, p.role, isFutsal),
      isStarting: true,
      playerNumber: getParticipantEffectivePlayerNumber(p),
    });
  }
  return { lineup, error: null };
}

const MIN_FORMAT = 5;
const MAX_FORMAT = 11;

/** Parse playing_style JSON {"format":"5:5"} to number (first part). */
export function parseFormatFromPlayingStyle(
  playingStyle: string | null | undefined
): number | null {
  if (!playingStyle || !playingStyle.trim()) return null;
  try {
    const o = JSON.parse(playingStyle) as { format?: string };
    const f = o?.format;
    if (typeof f !== 'string' || !f) return null;
    const part = f.split(':')[0]?.trim();
    const n = parseInt(part ?? '', 10);
    if (Number.isNaN(n) || n < MIN_FORMAT || n > MAX_FORMAT) return null;
    return n;
  } catch {
    return null;
  }
}

export { MIN_FORMAT, MAX_FORMAT };
